import { eulerPartnerPoints } from '@/config/chain/points'
import type { IsolatedPartnerPointsPerPair } from '@/config/chain/types'
import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import type { ELendVaultData } from '@/features/elend/logic/useELend'
import type { Address } from 'viem'
import type { ELendSourceData, ExposureItem, YieldExposure, YieldOpportunity } from '../types'
import { cleanVaultName } from './cleanVaultName'
import { makeYieldOpportunityId } from './makeYieldOpportunityId'

function createTokenFromVault(vault: ELendVaultData): Token | undefined {
  const tokenInfo = vault.underlyingTokenInfo
  if (!tokenInfo) return undefined

  const unitPriceUsd = vault.assetPrice?.toString() ?? '0'

  return new Token({
    symbol: TokenSymbol(tokenInfo.symbol),
    name: tokenInfo.name,
    decimals: tokenInfo.decimals,
    address: CheckedAddress(tokenInfo.address),
    unitPriceUsd,
    logoURI: tokenInfo.logoURI,
  })
}

function isActiveExposure(exposure: NonNullable<ELendVaultData['exposure']>[number]): boolean {
  const liquidationLTV = BigInt(exposure.liquidationLTV ?? 0)
  const targetTimestamp = BigInt(exposure.targetTimestamp ?? 0)
  const currentTimestamp = Math.floor(Date.now() / 1000)

  return liquidationLTV > 0n || targetTimestamp > BigInt(currentTimestamp)
}

function isCollateralOnlyVault(vault: ELendVaultData): boolean {
  if (vault.exposure === undefined) return false
  return vault.exposure.every((exposure) => !isActiveExposure(exposure))
}

function mapExposureData(vault: ELendVaultData): YieldExposure | null | undefined {
  // Data not available (on-chain fallback sets exposure to undefined)
  if (vault.exposure === undefined) {
    return null
  }

  // Genuinely no exposure (indexer confirms empty collateral list = collateral-only vault)
  if (vault.exposure.length === 0) {
    return undefined
  }

  const collaterals: ExposureItem[] = vault.exposure.map((e) => ({
    collateralAddress: e.collateral as Address,
    assetAddress: e.vaultAsset as Address | null,
    name: cleanVaultName(e.vaultName),
    borrowLTV: Number.parseInt(e.borrowLTV, 10),
    liquidationLTV: Number.parseInt(e.liquidationLTV, 10),
  }))

  return {
    type: 'vault-collaterals',
    collaterals,
  }
}

export function mapELendToYieldOpportunity(vault: ELendVaultData): YieldOpportunity {
  const chainId = E_LEND_CHAIN_ID
  const assetSymbol = vault.assetSymbol || vault.symbol
  const isCollateralOnly = isCollateralOnlyVault(vault)

  const hasPosition = vault.userDeposit !== undefined && Number(vault.userDeposit) > 0

  const totalBorrowedUsd =
    vault.totalBorrowed && vault.assetPrice
      ? NormalizedUnitNumber(Number(vault.totalBorrowed) * vault.assetPrice)
      : undefined

  const subAccountDeposits = vault.subAccountBalances
    ?.filter((sub) => Number(sub.depositAssetsFormatted) > 0)
    .map((sub) => ({
      subAccountId: sub.subAccountId,
      depositUsd: Number(sub.depositAssetsFormatted) * (vault.assetPrice ?? 0),
    }))

  const sourceData: ELendSourceData = {
    type: 'elend',
    subAccountId: vault.firstSubAccountWithBalance,
    isCollateralOnly,
    lendingAPY: vault.lendingAPY,
    intrinsicAPY: vault.intrinsicAPY,
    borrowAPY: vault.borrowAPY,
    totalBorrowedUsd,
    totalAssets: vault.totalAssets,
    subAccountDeposits: subAccountDeposits?.length ? subAccountDeposits : undefined,
  }

  return {
    id: makeYieldOpportunityId('elend', chainId, vault.address),
    source: 'elend',
    chainId,
    address: vault.address,
    name: vault.name,
    symbol: vault.symbol,
    assetSymbol,
    tokenInfo: vault.underlyingTokenInfo,
    token: vault.token ?? createTokenFromVault(vault),
    supplyAPY: vault.supplyAPY ?? 0,
    supplyApy30d: vault.supplyApy30d,
    tvlUsd: vault.tvlUsd,
    walletBalance: vault.walletBalance,
    walletBreakdown: vault.walletBreakdown,
    userDeposit: vault.userDeposit,
    userDepositUsd: vault.userDepositUsd,
    hasPosition,
    curatorName: vault.curatorName,
    curatorLogo: vault.curatorLogo,
    market: vault.market,
    points: vault.points,
    partnerPoints: assetSymbol
      ? (eulerPartnerPoints[assetSymbol] as IsolatedPartnerPointsPerPair[] | undefined)
      : undefined,
    exposure: mapExposureData(vault),
    sourceData,
  }
}

export function mapELendVaultsToYieldOpportunities(vaults: ELendVaultData[]): YieldOpportunity[] {
  return vaults.map(mapELendToYieldOpportunity)
}
